import { PremiumUpsell } from '@src/components/PremiumUpsell/PremiumUpsell';
import { WarningIcon } from '@src/components/icons/WarningIcon';
import { LockIcon } from '@src/components/icons/LockIcon';
import { createPortal } from 'react-dom';
import { Drawer } from '@src/components/Drawer/Drawer';
import { getPortalRoot } from '@src/components/portalRoot';
import { useMatchesMedia, MOBILE_HEADER_MQ } from '@src/components/layout/useMobileHeaderRecede';
import { isDotCom } from '@src/site';
import { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  CUSTOM_FIELD_TYPES,
  isBuiltinQuestion,
  questionVisibility,
  isQuestionEnabled,
  CUSTOM_FIELD_LIMIT,
  CUSTOM_TEXT_ANSWER_LIMIT,
  customFieldError,
  type CustomField,
  type CustomAnswer,
  type CustomFieldConfig,
} from '@lub0v/santa-utils';
import {
  Button,
  Divider,
  ChevronRightIcon,
  CheckIcon,
  CloseIcon,
  EyeIcon,
  EyeOffIcon,
  HintError,
  Hint,
  Input,
  Panel,
  PlusIcon,
  Select,
  Switch,
  Typography,
} from '@src/components';
import { PremiumFeatureBadge } from '@src/components/PremiumBadge/PremiumFeatureBadge';
import { TrashIcon } from '@src/components/icons/TrashIcon';
import { santaClient } from '@src/state/client';
import { CustomQuestionsForm, useCustomQuestions } from './CustomQuestions';
import { QuestionGrip, useQuestionSort } from './useQuestionSort';

function reorder<T>(items: T[], from: number, to: number): T[] {
  const next = [...items];
  const [item] = next.splice(from, 1);
  next.splice(to, 0, item);
  return next;
}

function blankQuestion(position: number): CustomField {
  return {
    title: '',
    enabled: true,
    type: 'string',
    required: false,
    visibleToSanta: true,
    visibleToOrganizer: true,
    config: {},
    position,
  };
}
function isBlankQuestion(field: CustomField): boolean {
  return (
    !field.id &&
    field.enabled &&
    !field.title.trim() &&
    field.type === 'string' &&
    !field.required &&
    field.visibleToSanta &&
    field.visibleToOrganizer &&
    Object.values(field.config).every((value) => value === undefined)
  );
}

function filledOptions(options: string[] = []): string[] {
  const next = [...options];
  while (next.length && !next[next.length - 1].trim()) next.pop();
  return next;
}
function withoutOptionDraft(question: CustomField): CustomField {
  return ['select', 'multiselect'].includes(question.type)
    ? {
        ...question,
        config: { ...question.config, options: filledOptions(question.config.options) },
      }
    : question;
}

export function CustomQuestionBuilder({
  boxKey,
  disabled = false,
  isPremium = false,
  onBlockedChange,
  demoFields,
  onDemoFieldsChange,
  decorated = true,
  premiumContext,
}: {
  boxKey: string;
  decorated?: boolean;
  premiumContext?: string;
  /** Local interactive product example; never reads or writes a game. */
  demoFields?: CustomField[];
  onDemoFieldsChange?: (fields: CustomField[]) => void;
  disabled?: boolean;
  isPremium?: boolean;
  onBlockedChange?: (blocked: boolean) => void;
}) {
  const { t } = useTranslation();
  const mobile = useMatchesMedia(MOBILE_HEADER_MQ);
  const [mobileEditorOpen, setMobileEditorOpen] = useState(false);
  const titleRef = useRef<HTMLInputElement>(null);
  const {
    fields,
    setFields,
    loading,
    error: loadError,
    retry,
  } = useCustomQuestions(boxKey, '', demoFields);
  useEffect(() => {
    if (demoFields) onDemoFieldsChange?.(fields);
  }, [demoFields, fields, onDemoFieldsChange]);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const focusAfterSave = useRef(false);
  useEffect(() => {
    if (!busy && focusAfterSave.current) {
      focusAfterSave.current = false;
      titleRef.current?.focus();
      titleRef.current?.scrollIntoView({ block: 'nearest' });
    }
  }, [busy, selectedIndex]);
  const [saved, setSaved] = useState(false);
  const [pendingDeletion, setPendingDeletion] = useState(false);
  const [announcement, setAnnouncement] = useState('');
  const [preview, setPreview] = useState(false);
  const [previewAnswers, setPreviewAnswers] = useState<CustomAnswer[]>([]);
  const [dirty, setDirty] = useState(false);
  const baseline = useRef<string | null>(null);
  const snapshot = (items: CustomField[]) =>
    JSON.stringify(
      items
        .filter((item) => !isBlankQuestion(item))
        .map(withoutOptionDraft)
        .map((item) => ({ ...item, enabled: true })),
    );
  useEffect(() => {
    if (!loading && !loadError && baseline.current === null) baseline.current = snapshot(fields);
  }, [loading, loadError, fields]);
  useEffect(() => {
    onBlockedChange?.(busy || loading || dirty || Boolean(loadError));
  }, [busy, loading, dirty, loadError, onBlockedChange]);
  const locked = disabled || busy || loading || Boolean(loadError);
  const index = Math.min(
    selectedIndex < 0 && !mobile ? 0 : selectedIndex,
    Math.max(0, fields.length - 1),
  );
  const field = fields[index];
  const builtin = field ? isBuiltinQuestion(field) : false;
  const closeMobileEditor = useCallback(() => {
    if (!busy) setMobileEditorOpen(false);
  }, [busy]);
  const questionLimit = isPremium ? CUSTOM_FIELD_LIMIT : 1;
  const customCount = fields.filter((item) => !isBuiltinQuestion(item)).length;
  const groupedFields = fields
    .map((item, i) => ({ item, i }))
    .sort((a, b) => Number(isBuiltinQuestion(b.item)) - Number(isBuiltinQuestion(a.item)));
  const questionTitle = (question: CustomField) =>
    `${fields.indexOf(question) + 1}. ${
      isBuiltinQuestion(question)
        ? t(`questions.types.${question.type}`)
        : question.title.trim() || t('questions.untitled')
    }`;
  const optionItems = filledOptions(field?.config.options);
  const optionRows = optionItems.length < 50 ? [...optionItems, ''] : optionItems;
  function update(next: CustomField[]) {
    setDirty(snapshot(next) !== baseline.current);
    setFields(next.map((item, position) => ({ ...item, position })));
    setSaved(false);
    setError(null);
  }
  async function toggleEnabled(enabled: boolean) {
    if (!field?.id || locked) return;
    const fieldId = field.id;
    const previous = isQuestionEnabled(field);
    setFields(fields.map((item) => (item.id === fieldId ? { ...item, enabled } : item)));
    setPreview(false);
    if (demoFields) return;
    setBusy(true);
    setError(null);
    try {
      const response = await santaClient.setQuestionEnabled({ key: boxKey, fieldId, enabled });
      if (!response.ok) throw new Error(response.error?.message || 'newBox.saveFailed');
      setAnnouncement(t('questions.inclusionSaved'));
    } catch (reason) {
      setFields(
        fields.map((item) => (item.id === fieldId ? { ...item, enabled: previous } : item)),
      );
      setError(reason instanceof Error ? reason.message : 'newBox.saveFailed');
    } finally {
      setBusy(false);
    }
  }
  const patch = (change: Partial<CustomField>) =>
    update(fields.map((f, i) => (i === index ? questionVisibility({ ...f, ...change }) : f)));
  const config = (change: Partial<CustomFieldConfig>) =>
    patch({ config: { ...field.config, ...change } });
  const {
    listRef: questionListRef,
    handleProps: questionHandleProps,
    rowClass: questionRowClass,
  } = useQuestionSort((from, to) => {
    if (isBuiltinQuestion(fields[from]) !== isBuiltinQuestion(fields[to])) return;
    update(reorder(fields, from, to));
    if (index === from) setSelectedIndex(to);
    else if (from < index && to >= index) setSelectedIndex(index - 1);
    else if (from > index && to <= index) setSelectedIndex(index + 1);
    setAnnouncement(t('questions.moved', { from: from + 1, to: to + 1 }));
  });
  const {
    listRef: optionListRef,
    handleProps: optionHandleProps,
    rowClass: optionRowClass,
  } = useQuestionSort((from, to) => {
    if (from >= optionItems.length || to >= optionItems.length) return;
    config({ options: reorder(optionItems, from, to) });
    setAnnouncement(t('questions.optionMoved', { from: from + 1, to: to + 1 }));
  });
  useEffect(() => {
    if (!mobile || !mobileEditorOpen) return;
    const questionList = questionListRef.current;
    const panel = questionList?.closest<HTMLElement>('.custom-question-panel');
    const previousOverflow = document.body.style.overflow;
    if (panel) panel.inert = true;
    document.body.style.overflow = 'hidden';
    const trapFocus = (event: KeyboardEvent) => {
      if (event.key !== 'Tab') return;
      const dialog = document.querySelector<HTMLElement>(
        '[data-testid="question-settings-drawer-panel"]',
      );
      const controls = Array.from(
        dialog?.querySelectorAll<HTMLElement>(
          'button:not([disabled]),input:not([disabled]),textarea:not([disabled]),[tabindex="0"]',
        ) ?? [],
      ).filter((node) => node.getClientRects().length > 0);
      const first = controls[0],
        last = controls[controls.length - 1];
      if (
        event.shiftKey &&
        (document.activeElement === first || document.activeElement === dialog)
      ) {
        event.preventDefault();
        last?.focus();
      } else if (
        !event.shiftKey &&
        (document.activeElement === last || document.activeElement === dialog)
      ) {
        event.preventDefault();
        first?.focus();
      }
    };
    document.addEventListener('keydown', trapFocus);
    return () => {
      if (panel) panel.inert = false;
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', trapFocus);
      requestAnimationFrame(() =>
        questionList?.querySelector<HTMLButtonElement>('[aria-current="true"]')?.focus(),
      );
    };
  }, [mobile, mobileEditorOpen, questionListRef]);
  function focusTitle() {
    requestAnimationFrame(() => {
      titleRef.current?.focus();
      titleRef.current?.scrollIntoView({ block: 'nearest' });
    });
  }
  function addQuestion() {
    if (mobile) setMobileEditorOpen(true);
    setPreview(false);
    setPreviewAnswers([]);
    const blankIndex = fields.findIndex(isBlankQuestion);
    if (blankIndex >= 0) setSelectedIndex(blankIndex);
    else if (customCount < questionLimit) {
      setSelectedIndex(fields.length);
      update([...fields, blankQuestion(fields.length)]);
    }
    focusTitle();
  }
  function deleteQuestion() {
    setPreview(false);
    setPreviewAnswers([]);
    if (field.id) setPendingDeletion(true);
    update(fields.filter((_, i) => i !== index));
    setSelectedIndex(Math.max(0, index - 1));
  }
  async function save() {
    // A fresh form opened after saving is local until the organizer fills it in.
    const submittedFields = fields.filter((f, i) => i === index || !isBlankQuestion(f));
    const definitions = submittedFields.map(withoutOptionDraft);
    const invalidIndex = definitions.findIndex((f) => customFieldError(f));
    if (invalidIndex >= 0) {
      setSelectedIndex(fields.indexOf(submittedFields[invalidIndex]));
      setError(customFieldError(definitions[invalidIndex]));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      if (demoFields) {
        const savedFields = definitions.map((item, position) => ({
          ...item,
          id: item.id ?? Math.max(0, ...fields.map((field) => field.id ?? 0)) + position + 1,
        }));
        baseline.current = snapshot(savedFields);
        setFields(savedFields);
        setDirty(false);
        setSaved(true);
        setPendingDeletion(false);
        if (mobile) setMobileEditorOpen(false);
        return;
      }
      const res = await santaClient.saveQuestions({ key: boxKey, fields: definitions });
      if (res.ok && res.data) {
        baseline.current = snapshot(res.data);
        setDirty(false);
        if (
          !mobile &&
          !builtin &&
          res.data.filter((item: CustomField) => !isBuiltinQuestion(item)).length < questionLimit
        ) {
          setFields([...res.data, blankQuestion(res.data.length)]);
          setSelectedIndex(res.data.length);
          setAnnouncement(t('questions.nextQuestion'));
          // Focus after the save lock is removed and the next input is committed.
          focusAfterSave.current = true;
        } else {
          setFields(res.data);
          setSelectedIndex(Math.min(index, res.data.length - 1));
        }
        setPreview(false);
        setPreviewAnswers([]);
        setSaved(true);
        setPendingDeletion(false);
        if (mobile) setMobileEditorOpen(false);
      } else setError(res.error?.message || 'newBox.saveFailed');
    } catch {
      setError('newBox.saveFailed');
    } finally {
      setBusy(false);
    }
  }
  function questionHint(question: CustomField): string {
    const parts: string[] = isBuiltinQuestion(question)
      ? []
      : [t(`questions.types.${question.type}`)];
    if (question.type === 'delivery_address')
      parts.push(
        t(
          `questions.types.${question.config.deliveryMode === 'pickup' ? 'pickup_point' : 'postal_address'}`,
        ),
      );
    if (isBuiltinQuestion(question) || !isQuestionEnabled(question))
      parts.push(t(isQuestionEnabled(question) ? 'questions.enabled' : 'questions.disabled'));
    if (['select', 'multiselect'].includes(question.type))
      parts.push(
        t('questions.optionCount', {
          count: (question.config.options ?? []).filter((option) => option.trim()).length,
        }),
      );
    if (question.type === 'string' || question.type === 'integer') {
      const min = question.type === 'string' ? question.config.minLength : question.config.min;
      const max = question.type === 'string' ? question.config.maxLength : question.config.max;
      let limits = '';
      if (min !== undefined && max !== undefined) limits = `${min}–${max}`;
      else if (min !== undefined) limits = `≥ ${min}`;
      else if (max !== undefined) limits = `≤ ${max}`;
      if (limits)
        parts.push(
          question.type === 'string' ? t('questions.characterLimits', { limits }) : limits,
        );
    }
    return parts.join(' · ');
  }
  const previewToggle =
    field && isQuestionEnabled(field) ? (
      <Button
        className="custom-question-builder__preview-toggle"
        variant="ghost"
        size="small"
        icon={preview ? <EyeOffIcon /> : <EyeIcon />}
        disabled={busy}
        aria-pressed={preview}
        aria-label={t(preview ? 'questions.editQuestion' : 'questions.previewQuestion')}
        title={t(preview ? 'questions.editQuestion' : 'questions.previewQuestion')}
        testId="custom-question-preview-toggle"
        onClick={() => {
          setPreview(!preview);
          setPreviewAnswers([]);
        }}
      />
    ) : null;
  const questionActions =
    !loading && !loadError && field && (mobile || (!preview && isQuestionEnabled(field))) ? (
      <footer className="custom-question-builder__footer">
        {pendingDeletion && (
          <Typography as="p" role="meta/helper">
            {t('questions.deleteNotice')}
          </Typography>
        )}
        <div className="custom-question-builder__save">
          {saved && !mobile && <span role="status">{t('game.settings.saved')}</span>}
          {field && !builtin && (
            <Button
              variant="danger"
              size="small"
              icon={<TrashIcon />}
              disabled={locked}
              aria-label={t('questions.delete')}
              title={t('questions.delete')}
              onClick={deleteQuestion}
            />
          )}
          {dirty && (
            <Button variant="primary" disabled={locked} loading={busy} onClick={() => void save()}>
              {t('questions.saveQuestion')}
            </Button>
          )}
        </div>
      </footer>
    ) : null;
  const workspace = (
    <div className="custom-question-builder__workspace" aria-busy={busy}>
      {loading && <p role="status">{t('questions.loading')}</p>}
      {loadError && (
        <>
          <HintError error={t(loadError)} />
          <Button onClick={retry}>{t('questions.retry')}</Button>
        </>
      )}
      {!loading && !loadError && !field && (
        <Typography as="p" role="meta/helper">
          {t(fields.length ? 'questions.selectQuestion' : 'questions.empty')}
        </Typography>
      )}
      {!mobile && field && (
        <div className="custom-question-builder__title-row">
          <Typography as="h3" role="title/card">
            {questionTitle(field)}
          </Typography>
          {previewToggle}
        </div>
      )}
      {preview && (
        <>
          <Typography as="p" role="meta/helper" className="custom-question-builder__preview-hint">
            {t('questions.previewHint')}
          </Typography>
          <Divider />
        </>
      )}
      {field && isQuestionEnabled(field) && preview && (
        <div className="custom-question-builder__preview" data-testid="custom-question-preview">
          <CustomQuestionsForm
            showHeading={false}
            fields={[
              {
                ...withoutOptionDraft(field),
                enabled: true,
                id: field.id ?? -1,
                title: field.title.trim() || t('questions.untitled'),
              },
            ]}
            answers={previewAnswers}
            onChange={setPreviewAnswers}
          />
        </div>
      )}
      {field && !preview && (
        <fieldset
          className="custom-question custom-question-builder__item"
          disabled={locked}
          aria-label={t('questions.settings')}
        >
          {(field.answerCount ?? 0) > 0 && (
            <div
              className="custom-question-builder__answer-warning"
              role="note"
              data-testid="question-existing-answers-warning"
            >
              <WarningIcon />
              <Typography as="p" role="body/sm">
                {t('questions.existingAnswersWarning', { count: field.answerCount })}
              </Typography>
            </div>
          )}
          {builtin && (
            <Typography as="p" role="meta/helper" testId="standard-question-description">
              {t(
                `questions.descriptions.${field.type === 'delivery_address' ? (field.config.deliveryMode === 'pickup' ? 'pickup_point' : 'postal_address') : field.type}`,
              )}
            </Typography>
          )}
          <Switch
            checked={isQuestionEnabled(field)}
            label={t('questions.collect')}
            hint={t(field.id ? 'questions.inclusionAutosaveHint' : 'questions.saveBeforeInclusion')}
            disabled={!field.id}
            onCheckedChange={(enabled) => void toggleEnabled(enabled)}
          />
          {isQuestionEnabled(field) && (
            <>
              {builtin ? (
                <>
                  {field.type === 'delivery_address' && (
                    <Select
                      label={t('questions.deliveryMode')}
                      value={field.config.deliveryMode ?? 'postal'}
                      options={[
                        { value: 'postal', label: t('questions.types.postal_address') },
                        ...(!isDotCom() || field.config.deliveryMode === 'pickup'
                          ? [{ value: 'pickup', label: t('questions.types.pickup_point') }]
                          : []),
                      ]}
                      onValueChange={(deliveryMode) =>
                        patch({ config: { deliveryMode: deliveryMode as 'postal' | 'pickup' } })
                      }
                    />
                  )}
                </>
              ) : (
                <div className="custom-question-builder__question-row">
                  <div className="custom-question-builder__custom-title-row">
                    <Input
                      ref={titleRef}
                      label={t('questions.questionNumber', {
                        number:
                          fields.slice(0, index).filter((question) => !isBuiltinQuestion(question))
                            .length + 1,
                      })}
                      testId="custom-question-title"
                      value={field.title}
                      maxLength={300}
                      onValueChange={(title) => patch({ title })}
                    />
                  </div>
                  <Select
                    className="custom-question-builder__type"
                    title={t(`questions.types.${field.type}`)}
                    label={t('questions.type')}
                    value={field.type}
                    options={CUSTOM_FIELD_TYPES.map((value) => ({
                      value,
                      label: t(`questions.types.${value}`),
                    }))}
                    onValueChange={(type) =>
                      patch({ type: type as CustomField['type'], config: {} })
                    }
                  />
                </div>
              )}
              {field.type === 'string' && (
                <details className="custom-question-builder__constraints" key={`limits-${index}`}>
                  <summary>{t('questions.textSettings')}</summary>
                  <Input
                    label={t('questions.placeholder')}
                    value={field.config.placeholder ?? ''}
                    maxLength={300}
                    onValueChange={(placeholder) => config({ placeholder })}
                  />
                  <div className="custom-question-builder__limits">
                    {(['minLength', 'maxLength'] as const).map((key) => (
                      <Input
                        key={key}
                        type="number"
                        step={1}
                        min={0}
                        max={CUSTOM_TEXT_ANSWER_LIMIT}
                        placeholder={
                          key === 'maxLength' ? String(CUSTOM_TEXT_ANSWER_LIMIT) : undefined
                        }
                        label={t(`questions.${key}`)}
                        value={field.config[key] === undefined ? '' : String(field.config[key])}
                        onValueChange={(v) =>
                          config({
                            [key]:
                              v === ''
                                ? undefined
                                : Math.max(0, Math.min(CUSTOM_TEXT_ANSWER_LIMIT, Number(v))),
                          })
                        }
                      />
                    ))}
                  </div>
                </details>
              )}
              {field.type === 'integer' && (
                <div className="custom-question-builder__limits">
                  {(['min', 'max'] as const).map((key) => (
                    <Input
                      key={key}
                      type="number"
                      step={1}
                      label={t(`questions.${key}`)}
                      value={field.config[key] === undefined ? '' : String(field.config[key])}
                      onValueChange={(v) => config({ [key]: v === '' ? undefined : Number(v) })}
                    />
                  ))}
                </div>
              )}
              {['select', 'multiselect'].includes(field.type) && (
                <div className="custom-question-builder__options">
                  <Typography as="h4" role="label/field">
                    {t('questions.answerOptions')}
                  </Typography>
                  <ul
                    className="custom-question-builder__option-list"
                    ref={optionListRef}
                    data-sort-list
                    data-testid="custom-question-options"
                  >
                    {optionRows.map((option, optionIndex) => (
                      <li
                        key={optionIndex}
                        data-sort-index={optionIndex}
                        className={`custom-question-builder__option ${optionIndex === optionItems.length ? 'is-draft' : ''} ${optionRowClass(optionIndex)}`}
                      >
                        <button
                          type="button"
                          className="question-sort-handle"
                          disabled={locked || optionIndex === optionItems.length}
                          {...optionHandleProps(optionIndex)}
                          aria-label={t('questions.reorderOption', { number: optionIndex + 1 })}
                          title={t('questions.dragHint')}
                        >
                          <QuestionGrip />
                        </button>
                        <Input
                          value={option}
                          maxLength={200}
                          placeholder={
                            optionIndex === optionItems.length
                              ? t('questions.optionPlaceholder')
                              : undefined
                          }
                          onEnter={() =>
                            requestAnimationFrame(() =>
                              optionListRef.current
                                ?.querySelector<HTMLInputElement>(
                                  `[data-sort-index="${optionIndex + 1}"] input`,
                                )
                                ?.focus(),
                            )
                          }
                          aria-label={t('questions.optionNumber', { number: optionIndex + 1 })}
                          onValueChange={(value) =>
                            config({
                              options: filledOptions(
                                optionRows.map((o, i) => (i === optionIndex ? value : o)),
                              ),
                            })
                          }
                        />
                        <Button
                          className="custom-question-builder__delete"
                          variant="ghost"
                          size="small"
                          icon={<TrashIcon />}
                          disabled={locked || optionIndex === optionItems.length}
                          aria-label={t('questions.deleteOption')}
                          title={t('questions.deleteOption')}
                          onClick={() =>
                            config({ options: optionItems.filter((_, i) => i !== optionIndex) })
                          }
                        />
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              <div className="custom-question-builder__settings">
                <Typography as="h4" role="label/field">
                  {t('questions.settings')}
                </Typography>
                {builtin && (
                  <Typography as="p" role="meta/helper">
                    {t(
                      `questions.visibility.${questionVisibility(field).visibleToOrganizer ? 'both' : 'santa'}`,
                    )}
                  </Typography>
                )}
                <Switch
                  checked={field.required}
                  label={t('questions.required')}
                  hint={t('questions.requiredHint')}
                  onCheckedChange={(required) => patch({ required })}
                />
                {!builtin && (
                  <>
                    <Switch
                      checked={field.visibleToSanta}
                      label={t('questions.visibleToSanta')}
                      hint={t('questions.visibleToSantaHint')}
                      onCheckedChange={(visibleToSanta) => patch({ visibleToSanta })}
                    />
                    <Switch
                      checked={field.visibleToOrganizer}
                      label={t('questions.visibleToOrganizer')}
                      hint={t('questions.visibleToOrganizerHint')}
                      onCheckedChange={(visibleToOrganizer) => patch({ visibleToOrganizer })}
                    />
                  </>
                )}
              </div>
            </>
          )}
        </fieldset>
      )}
      {error && !preview && (
        <div className="custom-question-builder__error">
          <Hint testId="question-save-error">{t(error)}</Hint>
        </div>
      )}
      {!mobile && questionActions}
    </div>
  );
  return (
    <Panel
      padding="none"
      wide
      edges={decorated ? { pieces: 3 } : undefined}
      className="settings__panel custom-question-panel"
      testId="custom-question-panel"
    >
      <header className="custom-question-builder__header">
        <div className="custom-question-builder__heading">
          <Typography as="h2" role="title/sub">
            {t('questions.organizerTitle')}
          </Typography>
        </div>
        <Typography as="p" role="meta/helper">
          {t('questions.organizerHint')}
          <br />
          {t('questions.sensitiveNotice')}
        </Typography>
      </header>
      <div className={`custom-question-builder ${mobile ? 'custom-question-builder--mobile' : ''}`}>
        <aside
          className="custom-question-builder__sidebar"
          aria-label={t('questions.questionList')}
        >
          {mobile && loading && <p role="status">{t('questions.loading')}</p>}
          {mobile && loadError && (
            <>
              <Hint>{t(loadError)}</Hint>
              <Button onClick={retry}>{t('questions.retry')}</Button>
            </>
          )}
          {fields.some(isBuiltinQuestion) && (
            <Typography as="h3" role="title/sub">
              {t('questions.standardQuestions')}
            </Typography>
          )}
          <ul
            className="custom-question-builder__list"
            ref={questionListRef}
            data-sort-list
            data-testid="custom-question-list"
          >
            {groupedFields.map(({ item, i }, displayIndex) => (
              <Fragment key={item.id ?? `new-${i}`}>
                {!isBuiltinQuestion(item) &&
                  (displayIndex === 0 ||
                    isBuiltinQuestion(groupedFields[displayIndex - 1].item)) && (
                    <li className="custom-question-builder__section-heading" role="presentation">
                      <Typography as="h3" role="title/sub">
                        {t('questions.customQuestions')}
                      </Typography>
                    </li>
                  )}
                <li
                  key={item.id ?? `new-${i}`}
                  data-sort-index={i}
                  data-question-kind={isBuiltinQuestion(item) ? 'builtin' : 'custom'}
                  className={`custom-question-builder__list-item ${index === i ? 'is-selected' : ''} ${!isQuestionEnabled(item) ? 'is-disabled' : ''} ${questionRowClass(i)}`}
                >
                  <button
                    type="button"
                    className="question-sort-handle"
                    disabled={locked}
                    {...questionHandleProps(i)}
                    aria-label={
                      isBuiltinQuestion(item)
                        ? questionTitle(item)
                        : t('questions.reorderQuestion', {
                            number:
                              fields.slice(0, i).filter((question) => !isBuiltinQuestion(question))
                                .length + 1,
                          })
                    }
                    title={t('questions.dragHint')}
                  >
                    <QuestionGrip />
                  </button>
                  <button
                    type="button"
                    className="custom-question-builder__select"
                    title={questionTitle(item)}
                    disabled={locked}
                    aria-current={index === i ? 'true' : undefined}
                    onClick={() => {
                      setSelectedIndex(i);
                      if (mobile) setMobileEditorOpen(true);
                      if (!isQuestionEnabled(item)) setPreview(false);
                      setPreviewAnswers([]);
                      setError(null);
                    }}
                    data-testid={
                      isBuiltinQuestion(item)
                        ? `builtin-question-select-${item.type}`
                        : `custom-question-select-${fields.slice(0, i).filter((question) => !isBuiltinQuestion(question)).length}`
                    }
                  >
                    <span>
                      <strong>
                        {item.id && (
                          <span
                            className={`custom-question-builder__enabled-check ${!isQuestionEnabled(item) ? 'custom-question-builder__enabled-check--disabled' : ''}`}
                            role="img"
                            aria-label={t(
                              isQuestionEnabled(item) ? 'questions.enabled' : 'questions.disabled',
                            )}
                            data-testid={
                              isQuestionEnabled(item)
                                ? 'question-enabled-check'
                                : 'question-disabled-cross'
                            }
                          >
                            {isQuestionEnabled(item) ? <CheckIcon /> : <CloseIcon />}
                          </span>
                        )}
                        {questionTitle(item)}
                      </strong>
                      <span title={questionHint(item)}>{questionHint(item)}</span>
                    </span>
                    <ChevronRightIcon />
                  </button>
                </li>
              </Fragment>
            ))}
          </ul>
          {customCount === 0 && (
            <div className="custom-question-builder__section-heading">
              <Typography as="h3" role="title/sub">
                {t('questions.customQuestions')}
              </Typography>
            </div>
          )}
          <Button
            className={`custom-question-builder__add ${!isPremium && customCount >= questionLimit ? 'custom-question-builder__add--locked' : ''}`}
            variant="secondary"
            icon={!isPremium && customCount >= questionLimit ? undefined : <PlusIcon />}
            disabled={locked || customCount >= questionLimit}
            onClick={addQuestion}
          >
            <span className="custom-question-builder__add-label">
              {!isPremium && customCount >= questionLimit && <LockIcon aria-hidden="true" />}
              {t('questions.add')}
            </span>
            {!isPremium && customCount >= questionLimit && (
              <PremiumFeatureBadge feature="questions">{t('newBox.appearance.premium')}</PremiumFeatureBadge>
            )}
          </Button>
          <Typography as="p" role="meta/helper" className="custom-question-builder__limit">
            {t('questions.count', { count: customCount, max: questionLimit })}
            {!isPremium && (
              <>
                <br />
                {t('questions.freeAllowance')}
              </>
            )}
          </Typography>
          {mobile && !isPremium && customCount >= questionLimit && (
            <PremiumUpsell context={premiumContext} feature="questions" testId="custom-questions-premium-panel" />
          )}
        </aside>
        {mobile ? (
          createPortal(
            <Drawer
              open={mobileEditorOpen}
              onClose={closeMobileEditor}
              side="end"
              footer={questionActions}
              titleActions={previewToggle}
              title={field ? questionTitle(field) : t('questions.untitled')}
              closeLabel={t('questions.backToList')}
              className="question-settings-drawer"
              testId="question-settings-drawer"
            >
              {workspace}
            </Drawer>,
            getPortalRoot(),
          )
        ) : (
          <div className="custom-question-builder__main">
            {workspace}
            {!isPremium && customCount >= questionLimit && (
              <PremiumUpsell context={premiumContext} feature="questions" testId="custom-questions-premium-panel" />
            )}
          </div>
        )}
      </div>
      <span className="custom-question-builder__announcement" role="status" aria-live="polite">
        {announcement}
      </span>
    </Panel>
  );
}
